/**
 * Valida CNPJ conforme algoritmo oficial da RFB (IN RFB nº 2.229/2024).
 * Suporta tanto o CNPJ tradicional (numérico) quanto o novo CNPJ alfanumérico
 * introduzido pela Receita Federal a partir de 2026.
 *
 * Estrutura esperada após sanitização:
 *   - 12 primeiros caracteres: alfanuméricos [0-9A-Z] (base do CNPJ)
 *   - 2 últimos caracteres: numéricos [0-9] (dígitos verificadores)
 *
 * Conversão dos caracteres: valor = charCode - 48 (ASCII - 48)
 * Ex: '0'→0, '9'→9, 'A'→17, 'Z'→42
 *
 * Aceita entrada com ou sem máscara (pontos, barra e traço).
 */
export function validateCnpj(cnpj?: string | null): boolean {
  if (!cnpj) return false;

  // Remove pontuação mas preserva letras e dígitos em maiúsculas
  const sanitized = cnpj
    .replace(/[.\-/\s]/g, "")
    .toUpperCase();

  if (sanitized.length !== 14) return false;

  // Estrutura: 12 alfanuméricos + 2 dígitos verificadores numéricos
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(sanitized)) return false;

  // Rejeita sequências com mesmo caractere repetido
  if (/^(.)\1{13}$/.test(sanitized)) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  // Converte char para valor via ASCII - 48 (mesmo que o backend C#)
  const charValue = (c: string) => c.charCodeAt(0) - 48;

  // Primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += charValue(sanitized[i]!) * weights1[i]!;
  }
  const rem1 = sum % 11;
  const dv1 = rem1 === 0 || rem1 === 1 ? 0 : 11 - rem1;
  if (dv1 !== parseInt(sanitized[12]!)) return false;

  // Segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += charValue(sanitized[i]!) * weights2[i]!;
  }
  sum += dv1 * weights2[12]!;
  const rem2 = sum % 11;
  const dv2 = rem2 === 0 || rem2 === 1 ? 0 : 11 - rem2;
  if (dv2 !== parseInt(sanitized[13]!)) return false;

  return true;
}

/**
 * Valida CPF ou CNPJ (numérico ou alfanumérico).
 * Equivalente ao DocumentoFiscalValidationUtil.ValidarCPFouCNPJ do backend.
 */
export function validateCpfOuCnpj(doc?: string | null): boolean {
  if (!doc) return false;

  const sanitized = doc.replace(/[.\-/\s]/g, "").toUpperCase();

  if (sanitized.length === 11) {
    // CPF — deve ser estritamente numérico
    if (!/^\d{11}$/.test(sanitized)) return false;
    if (/^(\d)\1{10}$/.test(sanitized)) return false;

    const weights1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(sanitized[i]!) * weights1[i]!;
    let rem = (sum * 10) % 11;
    if (rem === 10 || rem === 11) rem = 0;
    if (rem !== parseInt(sanitized[9]!)) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(sanitized[i]!) * weights2[i]!;
    rem = (sum * 10) % 11;
    if (rem === 10 || rem === 11) rem = 0;
    return rem === parseInt(sanitized[10]!);
  }

  if (sanitized.length === 14) {
    return validateCnpj(sanitized);
  }

  return false;
}
